
import { useState, useEffect } from "react";
import { Progress } from "@/components/ui/progress";
import { Trophy, Award, Star } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { loadTasks } from "@/utils/storage";
import { format, startOfMonth, endOfMonth, isWithinInterval } from "date-fns";

export default function TaskScore() {
  const [completionRate, setCompletionRate] = useState(0);
  const [totalTasks, setTotalTasks] = useState(0);
  const [completedTasks, setCompletedTasks] = useState(0);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const calculateScore = async () => {
      try {
        // Load tasks from storage
        const allTasks = await loadTasks();
        
        // Get current month's start and end dates
        const now = new Date();
        const monthStart = startOfMonth(now);
        const monthEnd = endOfMonth(now);
        
        // Filter tasks for current month only
        const monthTasks = allTasks.filter(task => {
          // Use completedAt if available, otherwise fall back to createdAt
          const taskDate = task.completedAt 
            ? new Date(task.completedAt) 
            : (task.createdAt ? new Date(task.createdAt) : new Date());
          
          // Include the task if it's in the current month
          return isWithinInterval(taskDate, { start: monthStart, end: monthEnd });
        });
        
        const completed = monthTasks.filter(task => task.completed).length;
        const total = monthTasks.length;
        
        setTotalTasks(total);
        setCompletedTasks(completed);
        setCompletionRate(total > 0 ? Math.round((completed / total) * 100) : 0);
        setLoading(false);
      } catch (error) {
        console.error("Error calculating task score:", error);
        toast({
          title: "Error",
          description: "Could not calculate task score",
          variant: "destructive",
        });
        setLoading(false);
      }
    };
    
    calculateScore();
  }, []);

  const getMotivationalMessage = () => {
    if (completionRate >= 100) {
      return "Perfect score! You're absolutely crushing it! 🏆";
    } else if (completionRate >= 80) {
      return "Excellent work! You're on fire! 🔥";
    } else if (completionRate >= 60) {
      return "Great job! Keep up the good work! 💪";
    } else if (completionRate >= 40) {
      return "You're making progress! Keep pushing forward! 🚀";
    } else if (completionRate >= 20) {
      return "You've started the journey! Every step counts! 👣";
    } else {
      return "Don't worry! Every new task completed is a victory! You can do this! 💯";
    }
  };
  
  const getScoreEmoji = () => {
    if (completionRate >= 90) return <Trophy className="w-12 h-12 text-yellow-500" />;
    if (completionRate >= 50) return <Award className="w-12 h-12 text-blue-500" />;
    return <Star className="w-12 h-12 text-purple-500" />;
  };

  const currentMonth = format(new Date(), 'MMMM yyyy');

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading score...</div>;
  }

  return (
    <div className="flex flex-col items-center space-y-6 py-8 px-4">
      <h2 className="text-2xl font-bold text-center text-blue-700 dark:text-blue-300">
        {currentMonth} Task Score
      </h2>
      
      <div className="flex flex-col items-center">
        {getScoreEmoji()}
        <div className="text-4xl font-bold mt-2 text-blue-600 dark:text-blue-400">
          {completionRate}%
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {completedTasks} of {totalTasks} tasks completed
        </div>
      </div>
      
      <div className="w-full max-w-md">
        <Progress value={completionRate} className="h-4" />
      </div>
      
      <div className="text-center text-lg font-medium p-4 rounded-lg bg-blue-50 dark:bg-blue-900/30 max-w-lg">
        {getMotivationalMessage()}
      </div>
      
      <div className="mt-4 grid grid-cols-3 gap-4 w-full max-w-md">
        <ScoreCard title="Perfect" value="100%" color="bg-green-100 text-green-700" />
        <ScoreCard title="Great" value="80%" color="bg-blue-100 text-blue-700" />
        <ScoreCard title="Good" value="60%" color="bg-yellow-100 text-yellow-700" />
      </div>
    </div>
  );
}

function ScoreCard({ title, value, color }: { title: string; value: string; color: string }) {
  return (
    <div className={`p-3 rounded-lg ${color} flex flex-col items-center justify-center`}>
      <div className="text-sm font-medium">{title}</div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}
